import React from 'react'
import { logger } from '../logging'
import { MyTmpVpnClient } from '@mytmpvpn/mytmpvpn-client'
import { VpnConfig, VpnConfigLimits } from '@mytmpvpn/mytmpvpn-common/models/vpnConfig'

import { RefreshButton, PageContainer } from '../components/Common'
import { useErrorContext } from '../ErrorContext'
import { PeanutsBalance } from '../account/PeanutsBalance'
import { VpnWithMetrics, listAllVpns } from './Home'
import { Vpns } from '../components/vpns/Vpns'
import { Configuration, ConfigurationSkeleton } from './Configuration'
import { isClientAuthenticated } from '../utils/authUtils'
import { View, Flex, Link } from '@aws-amplify/ui-react'
import { LocationWithProvidersInfo } from '@mytmpvpn/mytmpvpn-common/models/location'
import { useAuthenticator } from '../amplify'
import { VpnsSkeleton } from '../components/vpns/VpnsSkeleton'


async function refresh(client: MyTmpVpnClient,
  setBalance: any,
  setRefreshInProgress: any,
  setVpnsWithMetrics: any,
  setError: any,
) {
  if (!isClientAuthenticated(client)) {
    setError(new Error('Client not authenticated'))
    return
  }

  try {
    setRefreshInProgress(true)

    const [, balance] = await Promise.all([
      listAllVpns(client, setVpnsWithMetrics, setError),
      client.getPeanutsBalance()
    ])

    logger.log(`Balance: ${balance}`)
    setBalance(balance)
  } catch (error) {
    logger.error(error)
    setError(error)
  } finally {
    setRefreshInProgress(false)
  }
}

function DashboardInternal({ client,
  balance,
  setBalance,
  vpnConfigLimits,
  vpnConfig,
  setVpnConfig,
  vpnsWithMetrics,
  setVpnsWithMetrics,
  isDashboardLoading,
  locations }: {
    client: MyTmpVpnClient,
    balance: number,
    setBalance: any,
    vpnConfigLimits: VpnConfigLimits,
    vpnConfig: VpnConfig,
    setVpnConfig: any,
    vpnsWithMetrics: VpnWithMetrics[],
    setVpnsWithMetrics: any,
    isDashboardLoading: boolean,
    locations: LocationWithProvidersInfo[],
    displayInstructions: boolean,
    setDisplayInstructions: any
  }) {

  const [refreshInProgress, setRefreshInProgress] = React.useState<boolean>(false)
  const { setError } = useErrorContext()


  return (
    <View>
      <Flex className="dashboard-action-bar" justifyContent={'center'} alignItems={'center'} direction='row' gap='2'>
        <PeanutsBalance balance={balance} isLoading={refreshInProgress} />
        <RefreshButton
          isLoading={refreshInProgress}
          onClick={() => refresh(client,
            setBalance,
            setRefreshInProgress,
            setVpnsWithMetrics,
            setError)}
          disabled={refreshInProgress}
        />
      </Flex>
      {isDashboardLoading ? <VpnsSkeleton /> :
        <Vpns client={client}
          vpnsWithMetrics={vpnsWithMetrics}
          setVpnsWithMetrics={setVpnsWithMetrics}
          setError={setError}
          locations={locations}
          isLoading={refreshInProgress}
        />
      }
      {isDashboardLoading || !vpnConfigLimits ? <ConfigurationSkeleton /> :
        <Configuration
          balance={balance}
          vpnConfigLimits={vpnConfigLimits}
          vpnConfig={vpnConfig}
          setVpnConfig={setVpnConfig}
        />
      }
    </View>
  )
}

export function Dashboard({ client,
  balance,
  setBalance,
  vpnConfigLimits,
  vpnConfig,
  setVpnConfig,
  vpnsWithMetrics,
  setVpnsWithMetrics,
  isDashboardLoading,
  locations,
  displayInstructions,
  setDisplayInstructions
}: {
  client: MyTmpVpnClient,
  balance: number,
  setBalance: any,
  vpnConfigLimits: VpnConfigLimits,
  vpnConfig: VpnConfig,
  setVpnConfig: any,
  vpnsWithMetrics: VpnWithMetrics[],
  setVpnsWithMetrics: any,
  isDashboardLoading: boolean,
  locations: LocationWithProvidersInfo[],
  displayInstructions: boolean,
  setDisplayInstructions: any
}) {

  const { authStatus } = useAuthenticator((context) => [context.authStatus])

  return (
    <PageContainer>
      {authStatus === 'authenticated' ?
        <DashboardInternal
          client={client}
          balance={balance}
          setBalance={setBalance}
          vpnConfigLimits={vpnConfigLimits}
          vpnConfig={vpnConfig}
          setVpnConfig={setVpnConfig}
          vpnsWithMetrics={vpnsWithMetrics}
          setVpnsWithMetrics={setVpnsWithMetrics}
          isDashboardLoading={isDashboardLoading}
          locations={locations}
          displayInstructions={displayInstructions}
          setDisplayInstructions={setDisplayInstructions}
        /> :
        <Link href="/login">Please authenticate</Link>}
    </PageContainer>
  )
}
